import { useQuery } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";

import { DashboardsPageFrame } from "./dashboardKit";
import { RetellCallsReadout } from "./retellCallsReadout";
import {
  retellCallsCareSpaceBreakdownQueryOptions,
  retellCallsSummaryQueryOptions,
  retellCallsTransferReportQueryOptions,
} from "./retellCallsReactQuery";
import { DEEP, MUTED, PAGE_BG, TEAL, etYesterdayRange } from "./retellCallsTheme";

const inputStyle: CSSProperties = {
  fontSize: "0.85rem",
  padding: "0.3rem 0.5rem",
  borderRadius: 6,
  border: "1px solid #d1cdc7",
  background: "#fff",
};

function RefreshingBadge() {
  return (
    <span
      role="status"
      aria-live="polite"
      style={{
        fontSize: "0.75rem",
        fontWeight: 600,
        color: DEEP,
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
        borderRadius: 999,
        padding: "0.15rem 0.6rem",
      }}
    >
      Refreshing…
    </span>
  );
}

function LoadingState() {
  return (
    <div
      className="rounded-lg border border-border/70 bg-background/80 p-6 text-sm text-muted-foreground shadow-xs"
      role="status"
      aria-busy="true"
    >
      Loading Retell Calls…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700 shadow-xs">
      Error: {message}
    </div>
  );
}

export function RetellCallsDashboard() {
  const defaults = etYesterdayRange();
  const [draftStart, setDraftStart] = useState(defaults.start);
  const [draftEnd, setDraftEnd] = useState(defaults.end);
  const [applied, setApplied] = useState(defaults);

  const summaryQuery = useQuery(retellCallsSummaryQueryOptions(applied));
  const canLoadDetails = Boolean(summaryQuery.data) && !summaryQuery.isPlaceholderData;
  const transferQuery = useQuery(retellCallsTransferReportQueryOptions(applied, canLoadDetails));
  const careSpaceQuery = useQuery(
    retellCallsCareSpaceBreakdownQueryOptions(applied, canLoadDetails),
  );
  const dirty = draftStart !== applied.start || draftEnd !== applied.end;
  const data = summaryQuery.data;
  // Keeping the previous range on screen means only the very first load is a blank page.
  const showingStale =
    summaryQuery.isPlaceholderData ||
    transferQuery.isPlaceholderData ||
    careSpaceQuery.isPlaceholderData ||
    (Boolean(data) &&
      (summaryQuery.isFetching || transferQuery.isFetching || careSpaceQuery.isFetching));

  return (
    <DashboardsPageFrame
      className="min-h-full"
      data-retell-report-ready={
        data && transferQuery.data && careSpaceQuery.data && !showingStale ? "true" : undefined
      }
      style={{
        fontFamily: "inherit",
        background: PAGE_BG,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.66rem",
              letterSpacing: "0.14em",
              color: TEAL,
              fontWeight: 700,
            }}
          >
            RETELL CALLS
          </div>
          <h1
            style={{
              fontSize: "1.6rem",
              fontWeight: 800,
              color: "#16252e",
              letterSpacing: "-0.01em",
              marginTop: 2,
            }}
          >
            Daily readout
          </h1>
          {data ? (
            <p style={{ color: MUTED, fontSize: "0.85rem", marginTop: "0.2rem" }}>
              {data.start.replace("T", " ")} – {data.end.replace("T", " ")} ET
            </p>
          ) : null}
        </div>
        <div
          data-retell-report-controls
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <input
            type="datetime-local"
            value={draftStart}
            onChange={(event) => setDraftStart(event.target.value)}
            style={inputStyle}
          />
          <span style={{ color: MUTED, fontSize: "0.85rem" }}>to</span>
          <input
            type="datetime-local"
            value={draftEnd}
            onChange={(event) => setDraftEnd(event.target.value)}
            style={inputStyle}
          />
          <button
            type="button"
            onClick={() => setApplied({ start: draftStart, end: draftEnd })}
            style={{
              padding: "0.35rem 0.9rem",
              borderRadius: 6,
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
              background: dirty ? DEEP : "#eff6ff",
              color: dirty ? "#fff" : DEEP,
              border: `1px solid ${dirty ? DEEP : "#bfdbfe"}`,
              transition: "all 0.15s",
            }}
          >
            Apply
          </button>
          {showingStale ? <RefreshingBadge /> : null}
        </div>
      </div>

      {summaryQuery.isLoading ? <LoadingState /> : null}
      {summaryQuery.error ? (
        <ErrorState
          message={
            summaryQuery.error instanceof Error
              ? summaryQuery.error.message
              : "Failed to load Retell Calls"
          }
        />
      ) : null}
      {data && !summaryQuery.isLoading ? (
        <RetellCallsReadout
          data={data}
          retryTransfer={() => void transferQuery.refetch()}
          retryCareSpace={() => void careSpaceQuery.refetch()}
          {...(transferQuery.data && !transferQuery.error ? { transfer: transferQuery.data } : {})}
          {...(careSpaceQuery.data && !careSpaceQuery.error
            ? { careSpace: careSpaceQuery.data }
            : {})}
          {...(transferQuery.error
            ? {
                transferError:
                  transferQuery.error instanceof Error
                    ? transferQuery.error.message
                    : "Please retry shortly.",
              }
            : {})}
          {...(careSpaceQuery.error
            ? {
                careSpaceError:
                  careSpaceQuery.error instanceof Error
                    ? careSpaceQuery.error.message
                    : "Please retry shortly.",
              }
            : {})}
        />
      ) : null}
    </DashboardsPageFrame>
  );
}
